import { useEffect, useState } from "react";
import {
  AreaInfo,
  CardInfo,
  LayoutInfo,
  LayoutType,
  getAreaEntry,
  getCardInfo,
  getImage,
  getLayoutEntry,
  getVCard,
} from "../Utils/BackendInterface";
import juraLogoSvg from "../resources/img/Jura_Logo.svg";
import mailIcon from "../resources/img/mailIcon.png";
import mapIcon from "../resources/img/mapIcon.png";
import phoneIcon from "../resources/img/phoneIcon.png";
import worldIcon from "../resources/img/worldIcon.png";
import copy_icon from "../resources/img/copy_icon1.png";
import personIcon from "../resources/img/personIcon.png";

import { copyToClipboard, disclaimerBody, disclaimerTitle } from "../Utils/utils";
import { useTranslation } from "react-i18next";

const infoIcons = { mailIcon, phoneIcon, mapIcon, worldIcon };

export const Card = (props: { nfcId?: string }) => {
  const [t, i18n] = useTranslation();
  const [cardInfo, setCardInfo] = useState<CardInfo | undefined>();
  const [imgLink, setImgLink] = useState<string>();
  const [img, setImg] = useState<Blob | undefined>();
  const [vcard, setVCard] = useState("");

  const [areaInfo, setAreaInfo] = useState<AreaInfo | undefined>();
  const [layoutInfo, setLayoutInfo] = useState<LayoutInfo | undefined>();

  // helper states
  const [infoBoxLink, setInfoBoxLink] = useState("");

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const id = props?.nfcId ?? queryParams.get("id") ?? "";
    if (id) {
      getCardInfo(id).then((res) => {
        if (res) {
          setCardInfo(res);
          createVcardFile(res).then((url) => {
            if (url) setVCard(url);
          });

          // getAreaEntry(res.idArea).then((areaInfo) => setAddress(areaInfo?.address ?? ""));
          getLayoutEntry(res.idLayout).then((layoutInfo) => {
            setLayoutInfo(layoutInfo);
          });

          getAreaEntry(res.idArea).then((areaInfo) => setAreaInfo(areaInfo));

          getImage(id, res.pictureExtension ?? "").then((res) => {
            if (res) {
              console.log("had res");
              setImg(res);
            }
          });
        }
      });
    }
  }, []);

  /**
   * programmatically click the link when the link gets updated
   */
  useEffect(() => {
    if (infoBoxLink) {
      document.getElementById("infoBoxClicker")?.click();
    }
  }, [infoBoxLink]);

  const createVcardFile = async (cardInfo: CardInfo | undefined) => {
    if (!cardInfo) {
      return;
    }

    const vcard = await getVCard(cardInfo.nfcId);

    const objectURL = window.URL.createObjectURL(vcard);
    // window.open(objectURL);
    return objectURL;
  };

  const handleClickedMailLink = (email: string) => {
    setInfoBoxLink(`mailto:${email}`);
  };
  const handleClickedPhoneLink = (phoneNr: string) => {
    setInfoBoxLink(`tel:${phoneNr}`);
  };
  const handleClickedAddressForGoogleMapsWindow = (address: string) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`);
  };
  const handleClickedOpenURL = (url: string) => {
    if (!url?.includes("http")) {
      url = `https://${url}`;
    }
    window.open(url);
  };

  return (
    <div className="w-full h-full flex flex-col justify-start items-center">
      {/* Jura logo */}
      <div className="w-cardEntryWidth flex flex-row justify-end">
        <img src={juraLogoSvg} className="mt-7 h-card-logo" />
      </div>

      {/* image, name and title row */}
      <div className="w-cardEntryWidth flex flex-row justify-start items-center gap-6 mt-9 mb-6 max-w-lg">
        <div className="h-card-image max-w-card-image">
          {/* <img src={imgLink} className="h-full object-cover" /> */}
          <img src={img ? URL.createObjectURL(img) : ""} className="h-full object-cover" />
        </div>

        <div className="flex flex-col items-start">
          <div className="text-jura-gold-text text-xl leading-6">
            <span className="block m-0 h-fit">{cardInfo?.firstName}</span>
            <span className="block m-0 h-fit">{cardInfo?.surname}</span>
          </div>

          <span className="block mt-3 font-bold whitespace-pre-line break-all break-words">
            {customStringFormat(cardInfo?.jobDescription ?? "")}
          </span>
        </div>
      </div>

      {/* info section  */}
      <div className="w-full h-fit bg-jura-light-grey-bg flex flex-col justify-center items-center pt-6 pb-6">
        <div className="flex flex-col w-cardEntryWidth items-center gap-5 mb-9 max-w-lg">
          {/* the actual info entry row */}
          {cardInfo?.email && (
            <InfoBoxEntry
              infoKey="email"
              infoValue={cardInfo?.email ?? ""}
              infoIconSrc={infoIcons.mailIcon}
              clickHandler={() => {
                handleClickedMailLink(cardInfo.email);
              }}
            />
          )}
          {cardInfo?.telephone && (
            <InfoBoxEntry
              infoKey="telephone"
              infoValue={cardInfo?.telephone ?? ""}
              infoIconSrc={infoIcons.phoneIcon}
              clickHandler={() => {
                handleClickedPhoneLink(cardInfo.telephone);
              }}
            />
          )}
          {cardInfo?.telephone_1 && (
            <InfoBoxEntry
              infoKey="telephone_1"
              infoValue={cardInfo?.telephone_1 ?? ""}
              infoIconSrc={infoIcons.phoneIcon}
              clickHandler={() => {
                handleClickedPhoneLink(cardInfo.telephone_1 ?? "");
              }}
            />
          )}
          {cardInfo?.fax && ( // website only saved in the layout now
            <InfoBoxEntry
              infoKey="fax"
              infoValue={cardInfo?.fax ?? ""}
              infoIconSrc={infoIcons.worldIcon}
              clickHandler={() => {}}
            />
          )}
          {cardInfo?.mobile && (
            <InfoBoxEntry
              infoKey="mobile"
              infoValue={cardInfo?.mobile ?? ""}
              infoIconSrc={infoIcons.phoneIcon}
              clickHandler={() => {
                handleClickedPhoneLink(cardInfo.mobile ?? "");
              }}
            />
          )}
          {layoutInfo?.address && (
            <InfoBoxEntry
              infoKey="address"
              infoValue={customStringFormat(layoutInfo?.address ?? "")}
              infoIconSrc={infoIcons.mapIcon}
              clickHandler={() => {
                handleClickedAddressForGoogleMapsWindow(layoutInfo.address);
              }}
              copyValue={layoutInfo.address}
            />
          )}
          {cardInfo?.address && (
            <InfoBoxEntry
              infoKey="address_1"
              overrideInfoKey={
                layoutInfo?.type === LayoutType.DE_V3
                  ? layoutInfo.address_1_labels?.split(";")[cardInfo.chosen_address_1_index ?? 0]
                  : undefined
              }
              infoValue={customStringFormat(cardInfo?.address ?? "")}
              infoIconSrc={infoIcons.mapIcon}
              clickHandler={() => {
                handleClickedAddressForGoogleMapsWindow(cardInfo.address ?? "");
              }}
              copyValue={cardInfo.address}
            />
          )}
          {layoutInfo?.website && ( // website only saved in the layout now
            <InfoBoxEntry
              infoKey="website"
              infoValue={layoutInfo?.website ?? ""}
              infoIconSrc={infoIcons.worldIcon}
              clickHandler={() => {
                handleClickedOpenURL(layoutInfo.website);
              }}
            />
          )}
        </div>

        {/* the download vcf button */}
        {vcard && (
          <a
            href={vcard}
            download={`${cardInfo?.firstName ?? "card"}_${cardInfo?.surname ?? ""}`}
            className="flex flex-row min-w-download-vcf bg-white justify-center items-center p-4 mb-4 gap-4 cursor-pointer"
          >
            <img src={personIcon} className="w-8" />
            <span className="font-bold">{t("save-contact")}</span>
          </a>
        )}
      </div>

      <div className="w-cardEntryWidth mt-8 pb-4 text-sm text-center">
        <div className="font-semibold">{areaInfo?.disclaimerTitle}</div>
        <div className="whitespace-pre-line ">{areaInfo?.disclaimerText}</div>
      </div>

      <a className="hidden" id="infoBoxClicker" href={infoBoxLink} />
    </div>
  );
};

/**
 * The JSX element to be added as an info box.
 * @param infoValue
 * @returns
 */
const InfoBoxEntry = (props: {
  infoKey: string;
  infoValue: string;
  infoIconSrc: string;
  copyValue?: string;
  overrideInfoKey?: string; // don't use translation file for the info key, use value raw
  clickHandler: (infoKey: string) => void;
}) => {
  const [t, i18n] = useTranslation();
  return (
    <div
      key={`info_row_${props.infoKey}`}
      className="w-full flex-col bg-white relative"
      id={`info_row_${props.infoKey}`}
    >
      <img
        className="absolute right-0 z-1 w-8 mr-1 mt-1 cursor-pointer active:bg-jura-light-grey-bg"
        src={copy_icon}
        onClick={(event) => {
          console.log("is it?", props.copyValue);
          copyToClipboard(props.copyValue ?? props.infoValue);
          event?.stopPropagation();
        }}
      />
      <div className="w-full flex flex-row gap-6 justify-start items-center mb-6 mt-6">
        <div
          className="cursor-pointer ml-6 active:bg-jura-light-grey-bg select-none"
          onClick={() => {
            props.clickHandler(props.infoKey);
          }}
        >
          <img src={props.infoIconSrc} className="w-10  pointer-events-none" />
        </div>

        <div
          className="flex flex-col items-start"
          onClick={() => {
            props.clickHandler(props.infoKey);
          }}
        >
          <span className="block text-jura-gold-text text-lg">
            {props.overrideInfoKey ?? t(props.infoKey)}
          </span>
          <div className="block text-black font-semibold max-w-full whitespace-pre-line break-all break-words mr-5">
            {props.infoValue}
          </div>
        </div>
      </div>
    </div>
  );
};

// utis -------------------------
const customStringFormat = (inString: string): string => {
  return inString.replaceAll(",", "\n");
};
